import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RADIUS, SPACE } from '../theme/spacing';
import { TYPE } from '../theme/typography';
import { dismissToast, subscribeToastOutlet, type ToastRequest } from '../util/toastBridge';
import { useReduceMotion } from './Skeleton';

const DEFAULT_MS = 3000;
const WITH_ACTION_MS = 5000;

/**
 * Место, где рисуется уведомление из {@code showToast}. Один вывод — в корне приложения, ещё
 * по одному — внутри каждого нижнего листа (см. `toastBridge`). Цвета одни для всех тем:
 * тёмная плашка с белым текстом читается на любом фоне — так её и рисуют Android и iOS.
 *
 * @param bottomOffset подъём над нижним краем — например, над клавиатурой или полем ввода.
 */
export function ToastOutlet({ bottomOffset = 0 }: { bottomOffset?: number }) {
  const insets = useSafeAreaInsets();
  const reduceMotion = useReduceMotion();
  const [toast, setToast] = useState<ToastRequest | null>(null);
  const [opacity] = useState(() => new Animated.Value(0));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => subscribeToastOutlet(setToast), []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!toast) {
      opacity.setValue(0);
      return;
    }
    if (reduceMotion) {
      opacity.setValue(1);
    } else {
      Animated.timing(opacity, {
        toValue: 1,
        duration: 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
    const ms = toast.durationMs ?? (toast.actionLabel ? WITH_ACTION_MS : DEFAULT_MS);
    const id = toast.id;
    timer.current = setTimeout(() => dismissToast(id), ms);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [opacity, reduceMotion, toast]);

  if (!toast) return null;

  return (
    <View pointerEvents="box-none" style={[styles.layer, { bottom: insets.bottom + SPACE.lg + bottomOffset }]}>
      <Animated.View
        style={[styles.toast, { opacity }]}
        accessibilityRole="alert"
        accessibilityLiveRegion="polite"
        testID="app-toast">
        <Text style={styles.text} numberOfLines={3}>
          {toast.message}
        </Text>
        {toast.actionLabel ? (
          <Pressable
            onPress={() => {
              dismissToast(toast.id);
              toast.onAction?.();
            }}
            hitSlop={8}
            style={styles.action}
            accessibilityRole="button"
            accessibilityLabel={toast.actionLabel}>
            <Text style={styles.actionText}>{toast.actionLabel}</Text>
          </Pressable>
        ) : null}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  layer: { position: 'absolute', left: SPACE.lg, right: SPACE.lg, alignItems: 'center' },
  toast: {
    maxWidth: 560,
    width: '100%',
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.md,
    paddingHorizontal: SPACE.lg,
    paddingVertical: SPACE.sm,
    borderRadius: RADIUS.md,
    backgroundColor: '#1f2937',
    elevation: 8,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
  },
  text: { ...TYPE.body, color: '#f9fafb', flex: 1 },
  action: { minHeight: 44, justifyContent: 'center', paddingHorizontal: SPACE.sm },
  actionText: { ...TYPE.headline, color: '#93c5fd' },
});
